// Return approval gate (ESF-1052): the only place that decides whether a VAT return needs approval
// before "Mark as Submitted", and whether regenerating it voids that approval (ESF-1086). The excluded ("exempt from approval") list is separate from
// AUTO_SUBMISSION_CONFIG.enabledCountries (DS availability). Its interim source is config.js;
// when ESF-1085 serves it, only getExcludedCountries() changes.
import getClientConfig from './getClientConfig';
import { FilingTypeEnum } from './enums';
import { ALL_MESSAGES } from './logger';

// Warning banners shown above the return generation buttons; the gate names at most one in `warningMessage`.
export const RETURN_APPROVAL_WARNING_MESSAGES = [
  ALL_MESSAGES.RETURN_APPROVAL_UNAVAILABLE,
  ALL_MESSAGES.RETURN_APPROVAL_APPROVED_OUTDATED,
  ALL_MESSAGES.RETURN_APPROVAL_REJECTED_OUTDATED,
  ALL_MESSAGES.RETURN_APPROVAL_VOIDED,
  ALL_MESSAGES.RETURN_APPROVAL_EXPIRED
];

// The list reaches the client only if src/server/lib/app-config.js forwards it.
const getExcludedCountries = () => getClientConfig().returnApprovalExcludedCountries;

// Fail safe: without the list, every country is treated as exempt, so the page behaves as today instead of
// gating Direct Submission (FR) or MTD / ERIC / Digipoort (GB, DE, NL) returns.
export const isCountryExcludedFromApproval = (countryCode) => {
  const excludedCountries = getExcludedCountries();
  return !Array.isArray(excludedCountries) || excludedCountries.includes(String(countryCode || '').toUpperCase());
};

export const isReturnApprovalInScope = ({
  enableReturnApprovalWorkflow, isAsyncVatReturn, useV6CloseEndpointForVatReturn, filingTypeId, countryCode
}) => Boolean(enableReturnApprovalWorkflow && isAsyncVatReturn && useV6CloseEndpointForVatReturn)
  && filingTypeId === FilingTypeEnum.VATRETURN
  && !isCountryExcludedFromApproval(countryCode);

const closedGate = (status = null, markAsSubmittedTooltipKey = 'filingPage_ReturnApproval_MarkAsSubmittedNeedsApproval') =>
  ({ canSend: false, canMarkAsSubmitted: false, status, markAsSubmittedTooltipKey });
const resendGate = (status = null) => ({ ...closedGate(status), canSend: true });
const resendWithWarning = (warningMessage) => ({ ...resendGate(), warningMessage });

// Send is disabled while the current generation has an approval in progress or approved. It is enabled when there is
// none yet, when the current generation's approval was rejected (labelled "Resend", `isResend`), or when the latest one
// no longer applies (voided by a regenerate, expired, older generation).
const getGateForStatus = ({ loading, data, failed, errorStatus }) => {
  if (failed) {
    return errorStatus === 503
      ? { ...closedGate(null, 'filingPage_ReturnApproval_Unavailable'), warningMessage: ALL_MESSAGES.RETURN_APPROVAL_UNAVAILABLE }
      : closedGate();
  }
  if (loading || !data) return closedGate();
  if (!data.tierCount) return null;
  const { approval } = data;
  if (!approval) return resendGate();
  // The run's own tier count is fixed when it starts, so it wins over the tenant's current setting.
  const tierCount = approval.tierCount || data.tierCount;
  switch (approval.state) {
    case 'InProgress': {
      const openTiers = (approval.decisions || []).filter(d => d.status == null).map(d => d.tier);
      const tier = openTiers.length ? Math.max(...openTiers) : 1;
      return closedGate({ key: 'filingPage_ReturnApproval_Waiting', params: [tier, tierCount], tone: 'warning' });
    }
    case 'Approved':
      return data.isCurrentGeneration
        ? { ...closedGate({ key: 'filingPage_ReturnApproval_Approved', params: [tierCount, tierCount], tone: 'success' }), canMarkAsSubmitted: true }
        : resendWithWarning(ALL_MESSAGES.RETURN_APPROVAL_APPROVED_OUTDATED);
    case 'Rejected':
      return data.isCurrentGeneration
        ? { ...resendGate({ key: 'filingPage_ReturnApproval_Rejected', params: [approval.rejectedAtTier], tone: 'error' }), isResend: true }
        : resendWithWarning(ALL_MESSAGES.RETURN_APPROVAL_REJECTED_OUTDATED);
    case 'Voided':
      return resendWithWarning(ALL_MESSAGES.RETURN_APPROVAL_VOIDED);
    case 'Expired':
      return resendWithWarning(ALL_MESSAGES.RETURN_APPROVAL_EXPIRED);
    default:
      return closedGate();
  }
};

// Maps the returnApprovals Redux state of an in-scope return to the approval UI.
// Returns null when no approval is required (tierCount 0): the page behaves as today.
// `status` (with a `tone`: warning | error | success) renders as a coloured status button; `warningMessage` is raised
// by the returnApprovals controller as a warning banner above the buttons.
// `requested` (set by a successful Send) keeps Send disabled even if Status doesn't show the new
// run yet, because the run is started asynchronously.
export const getApprovalGate = (state = {}) => {
  const gate = getGateForStatus(state);
  return gate && state.requested ? { ...gate, canSend: false } : gate;
};

export const VAT_RETURN_REGENERATE_MESSAGE_KEY = 'filingPage_ConfirmationModalDisclaimerVatReturn';
export const VAT_RETURN_REGENERATE_VOIDS_APPROVAL_MESSAGE_KEY = 'filingPage_ConfirmationModalDisclaimerVatReturnVoidsApproval';

const APPROVAL_STATES_VOIDED_BY_REGENERATE = ['InProgress', 'Approved'];

// Regenerating a VAT return voids its approval server side (ESF-1052 [7a]), so the regenerate confirmation warns
// about it (ESF-1086). Decided from the returnApprovals Redux state only; it never calls the Status endpoint itself.
export const doesRegenerateVoidApproval = (returnApprovals) => {
  const data = returnApprovals && returnApprovals.data;
  if (!data || !data.tierCount || !data.approval) {
    return false;
  }
  return APPROVAL_STATES_VOIDED_BY_REGENERATE.includes(data.approval.state);
};
